import React, { useMemo } from 'react';
import { GridCellProps } from '@progress/kendo-react-grid';
import Card from '@components-clean/core/card';
import Grid from '@components-clean/core/grid-fe';
import { RagValueCell } from '@components-clean/core/grid-fe/custom-cells';
import { GridColumnProps } from '@components-clean/core/grid-fe/grid.interface';
import { InfoField } from '@components-clean/core/info-field';
import Loading from '@components-clean/core/loading/loading';
import { PanelDto } from '@interfaces/central-monitoring';
import { useLocalState, useTranslation } from '@hooks/index';
import KriOverviewApi from '@apis/bff-api/central-monitoring/kri-overview';

interface IProps {
    studyRef: string;
    panel?: PanelDto;
    datasetId?: number;
}

const KriHistoryGrid = ({ studyRef, panel, datasetId }: IProps) => {
    const { t }: { t: (key: string) => string } = useTranslation('cm', {
        keyPrefix: 'monitoringToolkit.kriOverview.cards.grid',
    });
    const { data, isLoading, isValidating } = KriOverviewApi.useGet(studyRef, panel?.reference, datasetId);
    const { state: localState } = useLocalState();
    const studyName = localState?.selectedStudy?.studyNumber;

    interface ITableInfo {
        columns: GridColumnProps[];
        data: { [key: string]: unknown }[];
    }

    const tableInfo = useMemo(() => {
        if (!data) return undefined;
        const info: ITableInfo = {
            columns: [
                {
                    id: 'siteName',
                    field: 'siteName',
                    title: t('columns.siteName'),
                    width: 1,
                    minWidth: 140,
                    locked: true,
                },
            ],
            data: [],
        };

        data.columns.forEach((column) => {
            info.columns.push({
                id: column,
                field: column,
                title: column,
                width: 1,
                minWidth: 100,
                filter: 'numeric',
                filterKey: 'rateOrScore',
                cells: { data: (props: GridCellProps) => RagValueCell({ ...props, numberFormat: '0.00' }) },
                ragColumn: true,
                singleValueRagColumn: true,
                multiValueExportTitles: {
                    rateOrScore: t('exportColumns.score'),
                    colour: t('exportColumns.colour'),
                },
            });
        });

        data.sites.forEach((site) => {
            const flattenedRow: { [key: string]: unknown } = {};
            flattenedRow.siteName = site.siteName;
            site.data.map((siteData) => {
                flattenedRow[siteData.dataSetDate] = { ...siteData };
                return flattenedRow;
            });
            info.data.push(flattenedRow);
        });

        return info;
    }, [data]);

    if (!tableInfo || isLoading || isValidating) {
        return (
            <Card className="placeholder-card" data-testid="grid" title={t('title')}>
                {isLoading || isValidating ? (
                    <Loading />
                ) : (
                    <InfoField message={t('emptyMessage')} type="Info" layout="center" />
                )}
            </Card>
        );
    }

    return (
        <Grid
            title={t('title')}
            maxHeight="auto"
            testDataId="kri-overview-grid"
            data={tableInfo.data}
            columns={tableInfo.columns}
            loading={isLoading}
            useLoadingOverlay
            scrollGridRight
            exportFilename={`${studyName}_${panel?.plotTitle}_KRI_History`}
            showAddIcon={false}
        />
    );
};

export default KriHistoryGrid;
